<template>
  <div class="chip-input">
    <label
      class="flex flex-wrap content-between w-full px-2 py-1 bg-white border border-gray-300 chip-input-container rounded-md shadow-sm ring-gray-900 sm_text-sm focus-within_ring-2 cursor-text"
      :for="`vchipinput-${name}`"
      data-testid="chips"
    >
      <div
        v-for="(chip, i) of chips"
        :key="chip"
        class="flex items-center px-3 py-1 m-1 rounded-full cursor-default chip bg-gray-50"
        :data-testid="`chip-${i}`"
        @click.stop.prevent=""
      >
        {{ chip }}
        <button class="p-0 ml-1 bg-transparent appearance-none" type="button" @click="deleteChip(i)">
          <XMarkIcon class="w-4 h-4 text-gray-500" />
        </button>
      </div>

      <div ref="chip-input" :name="name" class="flex items-center flex-auto">
        <input
          :id="`vchipinput-${name}`"
          v-model="currentInput"
          :type="type"
          :name="name"
          :placeholder="chips.length ? '' : placeholder"
          data-testid="chip-input"
          class="flex-auto w-full px-1 py-0 placeholder-gray-500 outline-none"
          @keydown.tab.prevent="validateAndSaveChip"
          @keydown.enter.prevent="validateAndSaveChip"
          @keydown.delete="deleteOnBackspace"
          @paste="addPastedInputToChips"
          @input="handleInput"
          @blur.self="validateAndSaveChip"
        />
      </div>
    </label>
    <div v-if="errors && errors.chipError" class="mt-1 ml-1 text-sm text-red-800 error" data-testid="chip-input-error">
      {{ errors.chipError }}
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { validate as validateEmail } from 'email-validator'

const props = defineProps({
  type: {
    type: String,
    default: 'email',
  },
  name: {
    type: String,
    default: 'email',
  },
  csvRegex: {
    type: RegExp,
    default: () => /[^,\s]+/g,
  },
  value: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
  dict: {
    type: Object,
    default: () => {
      return {
        custom: {
          email: {
            required: () => 'This field is required.',
            email: () => 'This is not a valid email.',
          },
        },
      }
    },
  },
  placeholder: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['chip-input-error', 'focus-prev-input', 'focus-next-input', 'chip-added'])

const currentInput = ref('')
const chips = ref(props.value ?? [])
const errors = reactive<{ chipError: string | null }>({ chipError: null })

function handleInput(e: Event) {
  clearChipErrors()

  if ((e as InputEvent).data === ',' || (e as InputEvent).data === ' ') {
    validateAndSaveChip(e as KeyboardEvent)
  }
}

function validateAndSaveChip(e: Event) {
  trimInput(e as FocusEvent)

  if (validateEmail(currentInput.value)) {
    saveChip()
    return
  }

  const emptyInput = !currentInput.value
  const tabKeyPressed = (e as KeyboardEvent).keyCode === 9

  if (emptyInput) {
    if (tabKeyPressed) {
      handleTabKey(e as KeyboardEvent)
    }

    return
  }

  setChipError('This is not a valid email')
}

function trimInput(e: FocusEvent) {
  currentInput.value = currentInput.value.replace(/^\s+|\s+$/gm, '')

  if ((e.target as HTMLInputElement).value.endsWith(',')) {
    currentInput.value = currentInput.value.slice(0, -1)
  }
}

function clearChipErrors() {
  emit('chip-input-error', false)
  errors.chipError = null
}

function setChipError(message: string) {
  errors.chipError = message
  emit('chip-input-error', true)
}

function handleTabKey(e: KeyboardEvent) {
  e.shiftKey ? emit('focus-prev-input') : emit('focus-next-input')
}

function saveChip() {
  const isNotDuplicate = chips.value.indexOf(currentInput.value) === -1
  const isNotEmpty = currentInput.value.length

  if (isNotDuplicate) {
    if (isNotEmpty) {
      chips.value.push(currentInput.value)
      emit('chip-added', chips.value)
      clearChipErrors()
      currentInput.value = ''
    }
  } else {
    errors.chipError = 'This email was already added'
  }
}

function deleteChip(index: number) {
  chips.value.splice(index, 1)
}

function deleteOnBackspace(e: KeyboardEvent) {
  e.which === 8 && currentInput.value === '' && chips.value.splice(chips.value.length - 1)
}

function addPastedInputToChips(e: ClipboardEvent) {
  e.preventDefault()

  if (!e.clipboardData) return

  const pastedText = e.clipboardData.getData('text')
  // separate the CSV pasted input into an array
  const regexSeparatedData = pastedText.match(props.csvRegex)
  // check to see if there are duplicates in the pasted text
  const commaSeparatedData = regexSeparatedData!.filter((data, index) => regexSeparatedData!.indexOf(data) === index)
  const [validData, invalidData] = getValidChipData(commaSeparatedData)

  addArrayToChips(validData)
  handleChipError(invalidData)
}

function getValidChipData(commaSeparatedData: string[]): [string[], string[]] {
  return commaSeparatedData.reduce(
    ([valid, invalid]: [string[], string[]], cur) => {
      if (validateEmail(cur)) {
        return [valid.concat(cur), invalid]
      } else {
        return [valid, invalid.concat(cur)]
      }
    },
    [[], []],
  )
}

function addArrayToChips(toBeChipped: string[]) {
  if (!toBeChipped.length) return

  const toBeAdded = toBeChipped.filter(toBeChipped => !chips.value.includes(toBeChipped))
  chips.value = [...chips.value, ...toBeAdded]
  emit('chip-added', chips.value)
}

function handleChipError(invalidData: string[]) {
  if (!invalidData.length) {
    document.execCommand('delete') // Clear selected text if valid input
    clearChipErrors()
    return
  }

  if (invalidData.length === 1) {
    setChipError(`Email “${invalidData[0]}” is not valid and was not added`)
  } else if (invalidData.length <= 10) {
    setChipError(`Emails “${invalidData.join('”, “')}” are not valid and were not added`)
  } else {
    setChipError(`Invalid clipboard data not added`)
  }
}
</script>

<style scoped lang="stylus">
.chip-input
    input[type='email']
      @apply border-0 shadow-none
</style>
